import { format, parse } from "date-fns";

import { DATE } from "@utils/constants";

import { DeliveryMethod, InvoiceType, LabelPrint, OrderFields, OrderStep } from "../constants";
import { TOrderFormData } from "../models/orderForm";
import { TShipment } from "../shipmentTypes";

const mapShipmentToOrder = (shipment: TShipment): TOrderFormData => ({
  // These set to true initially and will be changed with useEffect
  [OrderFields.IS_DOMESTIC]: true,
  [OrderFields.IS_INTRA_EUROPE]: false,
  [OrderFields.IS_FROM_ORIGIN]: true,

  [OrderFields.CURRENT_STEP]: OrderStep.PackageInfo,
  [OrderFields.PROMO_CODE]: shipment.coupon || undefined,
  [OrderFields.OFFER_GROUP_ID]: shipment.offerGroupId,
  [OrderFields.SHIPMENT_ID]: shipment.id,
  [OrderFields.SHIPMENT_SIGNED_IN]: true,
  [OrderFields.AFFILIATE_ID]: shipment.affiliateGuid || undefined,
  [OrderFields.AFFILIATE_RETURN_URL]: undefined,

  [OrderFields.STARTING_COUNTRY]: shipment.senderAddress.countryCode,
  [OrderFields.STARTING_COUNTRY_NAME]: shipment.senderAddress.country,
  [OrderFields.DESTINATION_COUNTRY]: shipment.receiverAddress.countryCode,
  [OrderFields.DESTINATION_COUNTRY_NAME]: shipment.receiverAddress.country,
  [OrderFields.PACKAGE_SIZE]: shipment.dimension,
  [OrderFields.PACKAGE_WEIGHT]: shipment.parcelWeight || undefined,
  [OrderFields.DELIVERY_METHOD]: shipment.deliveryMethod as DeliveryMethod | null | undefined,

  [OrderFields.SENDER_NAME]: shipment.senderAddress.name,
  [OrderFields.SENDER_PHONE_NUMBER]: shipment.senderAddress.phone,
  [OrderFields.SENDER_EMAIL]: shipment.senderAddress.email,
  [OrderFields.RETURN_LOCKER]: shipment.senderAddress.pudoId,
  [OrderFields.RETURN_LOCKER_NAME]: shipment.senderAddress.lockerName,
  [OrderFields.SENDER_STREET]: shipment.senderAddress.street,
  [OrderFields.SENDER_HOUSE_NO]: shipment.senderAddress.streetNo,
  [OrderFields.SENDER_FLAT_NO]: shipment.senderAddress.flatNo,
  [OrderFields.SENDER_CITY]: shipment.senderAddress.city,
  [OrderFields.SENDER_ZIP_CODE]: shipment.senderAddress.postalCode,
  [OrderFields.SENDER_COMMENTS]: shipment.senderAddress.contactInfo,
  [OrderFields.LABEL_PRINT]: shipment.willCustomerPrintLabel ? LabelPrint.PRINT : LabelPrint.COURIER,
  [OrderFields.PICKUP_DATE]: shipment.pickup?.pickupDate
    ? format(parse(shipment.pickup.pickupDate, "yyyy-MM-dd", new Date()), DATE.DATE_TEMPLATE)
    : undefined,
  [OrderFields.PICKUP_TIME_FROM]: shipment.pickup?.pickupTimeFrom,
  [OrderFields.PICKUP_TIME_TILL]: shipment.pickup?.pickupTimeTo,
  [OrderFields.SENDER_AS_CONTACT_PERSON]:
    !!(shipment.senderAddress.contactName && shipment.senderAddress.contactName === shipment.senderAddress.name) &&
    !!(shipment.senderAddress.contactPhone && shipment.senderAddress.contactPhone === shipment.senderAddress.phone) &&
    !!(shipment.senderAddress.contactEmail && shipment.senderAddress.contactEmail === shipment.senderAddress.email),
  [OrderFields.CONTACT_PERSON_NAME]: shipment.senderAddress.contactName,
  [OrderFields.CONTACT_PERSON_PHONE_NUMBER]: shipment.senderAddress.contactPhone,
  [OrderFields.CONTACT_PERSON_EMAIL]: shipment.senderAddress.contactEmail,

  [OrderFields.RECEIVER_ADDRESS_ID]: shipment.receiverAddress.addressId,
  [OrderFields.RECEIVER_NAME]: shipment.receiverAddress.name,
  [OrderFields.RECEIVER_PHONE_NUMBER]: shipment.receiverAddress.phone,
  [OrderFields.RECEIVER_EMAIL]: shipment.receiverAddress.email || undefined,
  [OrderFields.RECEIVER_LOCKER]: shipment.receiverAddress.pudoId,
  [OrderFields.RECEIVER_LOCKER_NAME]: shipment.receiverAddress.lockerName,
  [OrderFields.RECEIVER_STREET]: shipment.receiverAddress.street,
  [OrderFields.RECEIVER_HOUSE_NO]: shipment.receiverAddress.streetNo,
  [OrderFields.RECEIVER_FLAT_NO]: shipment.receiverAddress.flatNo,
  [OrderFields.RECEIVER_CITY]: shipment.receiverAddress.city,
  [OrderFields.RECEIVER_ZIP_CODE]: shipment.receiverAddress.postalCode,
  [OrderFields.RECEIVER_COMMENTS]: shipment.receiverAddress.contactInfo,

  [OrderFields.HAS_NEW_ADDRESS]: false,
  [OrderFields.NEW_ADDRESS_NAME]: "",
  [OrderFields.NEW_ADDRESS_PHONE]: "",
  [OrderFields.NEW_ADDRESS_EMAIL]: "",
  [OrderFields.NEW_ADDRESS_LOCKER]: "",
  [OrderFields.NEW_ADDRESS_LOCKER_NAME]: "",
  [OrderFields.NEW_ADDRESS_STREET]: "",
  [OrderFields.NEW_ADDRESS_HOUSE_NO]: "",
  [OrderFields.NEW_ADDRESS_FLAT_NO]: "",
  [OrderFields.NEW_ADDRESS_CITY]: "",
  [OrderFields.NEW_ADDRESS_ZIP_CODE]: "",

  [OrderFields.INVOICE_TYPE]:
    !!shipment.invoice.privateName && !!shipment.invoice.privateEmail ? InvoiceType.PRIVATE : InvoiceType.COMPANY,
  [OrderFields.INVOICE_NAME]: shipment.invoice.privateName,
  [OrderFields.INVOICE_EMAIL]: shipment.invoice.privateEmail,
  [OrderFields.INVOICE_COMPANY_NAME]: shipment.invoice.companyName,
  [OrderFields.INVOICE_COMPANY_EMAIL]: shipment.invoice.emailAddress,
  [OrderFields.INVOICE_REGISTRY_CODE]: shipment.invoice.registryCode,
  [OrderFields.INVOICE_VAT_NUMBER]: shipment.invoice.vatNumber,
  [OrderFields.INVOICE_REGISTRATION_ADDRESS]: shipment.invoice.companyRegistrationAddress,

  [OrderFields.TERMS_AGREEMENT]: true, // If order is saved, then terms are already agreed

  [OrderFields.PAYMENT_METHOD]: null,
  [OrderFields.PRICE]: shipment.price ?? undefined,
  [OrderFields.ORIGINAL_PRICE]: shipment.originalPrice ?? undefined,
});
export default mapShipmentToOrder;
